import type { QueryClient } from '@tanstack/react-query';
import type { Project } from '@/api/client';
import type { GlossaryData } from '@/api/glossaryModel';
import { getProjectFilesQueryOptions } from '@/components/ProjectFilesPopover/projectFilesPopoverQuery';
import { getProjectNotesQueryOptions } from '@/components/ProjectNotesPopover/projectNotesPopoverQuery';
import { getProjectSessionsQueryOptions } from '@/components/ProjectSessionsModal/projectSessionsQuery';
import { createInFlightTracker } from '@/lib/inFlightByKey';
import { projectEntityQueryKey } from '@/lib/projectEntityCache';
import { PROJECT_DRAFTS_QUERY_ROOT } from '@/lib/projectFilterRealtime';
import { acceptServerList } from '@/lib/serverListSync';
import type { ProjectStatus } from '@/utils/projectStatuses';
import { DEADLINE_FIELDS } from '@stages/stages';
import { glossaryQueryKey } from '../stress/stressCache';
import { projectTasksQueryKey } from '../tasks/taskCache';

/** Этап нового проекта и пустого черновика (server/src/constants/projectStates.ts: DEFAULT_PROJECT_STATE). */
export const DRAFT_STATE = 'Добавлен' satisfies ProjectStatus;

export const projectDraftsQueryKey = () => [...PROJECT_DRAFTS_QUERY_ROOT];

/*
 * Скалярные поля, которые у пустого черновика null: server/src/models/projectDrafts.ts → DRAFT_BLANK_NULL_COLUMNS.
 * Клиент берёт только те, что есть в сущности Project (name_note … комментарии отметок); решает всё равно сервер.
 */
const BLANK_NULL_FIELDS = [
  'name_note',
  ...DEADLINE_FIELDS,
  'review_check_type',
  'corrections_number',
  'author_sheets',
  'tracks_count',
  'size',
  'asta',
  'ase',
  'rin',
  'paper_book_plan',
  'theme',
  'rights',
  'project_type',
  'edition_id',
  'editor_id',
  'time',
  'project_remote_note',
  'project_phonogram_note',
  'project_dp_note',
  'project_pdf_note',
  'project_approved_note',
  'project_done_note',
] as const satisfies readonly (keyof Project)[];

/** Отметки — у пустого черновика все серые (0): DRAFT_BLANK_ZERO_COLUMNS. */
const BLANK_ZERO_FIELDS = [
  'project_remote',
  'project_phonogram',
  'project_dp',
  'project_pdf',
  'project_approved',
  'project_done',
] as const satisfies readonly (keyof Project)[];

/** Флаги, которые у пустого черновика false/null: DRAFT_BLANK_FALSE_COLUMNS. */
const BLANK_FALSE_FIELDS = [
  'no_edits',
  'is_backlog',
  'is_archived',
  'is_project_highlighted',
] as const satisfies readonly (keyof Project)[];

/**
 * Черновик ничего не содержит: зеркало isDraftEmpty (server/src/models/projectDrafts.ts), в пределах того, что
 * несёт сущность Project. Люди, контракты, файлы, заметки, треки фонотеки и скаляры проверяются; тексты книги,
 * глоссарий, задачи и комментарии ячеек в сущности нет — считаем «неизвестно, пусто»: сервер перепроверит перед
 * удалением и ответит deleted: false.
 */
export function isBlankDraft(project: Project): boolean {
  if (project.name.trim() !== '') return false;
  if (project.state !== DRAFT_STATE) return false;
  if ((project.pages ?? 0) !== 0) return false;
  if (BLANK_NULL_FIELDS.some((field) => project[field] != null)) return false;
  if (BLANK_FALSE_FIELDS.some((field) => project[field])) return false;
  if (BLANK_ZERO_FIELDS.some((field) => (project[field] ?? 0) !== 0)) return false;
  const lists = [
    project.readers,
    project.authors,
    project.producers,
    project.techeditors,
    project.correctors,
    project.contractors,
    project.project_contracts,
    project.files,
    project.notes,
  ];
  if (lists.some((list) => (list?.length ?? 0) > 0)) return false;
  return (project.fonoteka_track_count ?? 0) === 0;
}

/**
 * Пустой черновик, каким его вернёт сервер сразу после создания (orchestrateProjectCreate без входных полей): им карточка
 * открывается до ответа. Поля, которые знает только сервер (даты, ревизия файлов, имена справочников), дополнит ответ.
 */
export function blankDraft(id: string): Project {
  return {
    id,
    is_draft: true,
    name: '',
    state: DRAFT_STATE,
    pages: 0,
    progress: 0,
    ...(Object.fromEntries(BLANK_NULL_FIELDS.map((field) => [field, null])) as Pick<Project, (typeof BLANK_NULL_FIELDS)[number]>),
    ...(Object.fromEntries(BLANK_FALSE_FIELDS.map((field) => [field, false])) as Pick<Project, (typeof BLANK_FALSE_FIELDS)[number]>),
    ...(Object.fromEntries(BLANK_ZERO_FIELDS.map((field) => [field, 0])) as Pick<Project, (typeof BLANK_ZERO_FIELDS)[number]>),
    readers: [],
    authors: [],
    project_contracts: [],
    producers: [],
    techeditors: [],
    correctors: [],
    status_events: [],
    notes: [],
    files: [],
    sessions: [],
    fonoteka_track_count: 0,
  };
}

/**
 * Ответ сервера на создание поверх сущности в кэше: сущность — источник истины, но из неё берётся только то, что клиент
 * изменил в пустом черновике (правки, набранные до ответа); остальное — как посчитал сервер.
 */
export function mergeCreatedDraft(server: Project, cached: Project): Project {
  const blank = blankDraft(cached.id) as unknown as Record<string, unknown>;
  const edits = Object.entries(cached).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(blank[key]));
  return { ...server, ...Object.fromEntries(edits) };
}

/** Глоссарий нового проекта — каким его отдаёт GET /projects/:id/glossary без текста и записей (общих тоже: их отбирает текст книги). */
const BLANK_GLOSSARY: GlossaryData = {
  text_status: 'none',
  text_files: [],
  text_run: null,
  glossary_files: [],
  glossary_file_ids: [],
  active_import: null,
  entries: [],
  shared: [],
};

/** Списки блоков карточки: у только что созданного проекта они пусты */
const blankDraftListKeys = (id: string) => [
  getProjectFilesQueryOptions(id).queryKey,
  getProjectNotesQueryOptions(id).queryKey,
  projectTasksQueryKey(id),
  getProjectSessionsQueryOptions(id).queryKey,
  glossaryQueryKey(id),
];

/**
 * Новый черновик в кэш целиком: сущность и пустые списки блоков карточки — карточка открывается без загрузок.
 * Списки помечены устаревшими: открытые блоки перечитают их в фоне (запросы дождутся создания) и сверятся с сервером.
 */
export function seedBlankDraft(queryClient: QueryClient, id: string): void {
  queryClient.setQueryData<Project>(projectEntityQueryKey(id), blankDraft(id));
  // Файлы — через снимок с ревизией (lib/serverListSync): у нового проекта ревизия 0, загрузки ложатся поверх него
  acceptServerList(queryClient, getProjectFilesQueryOptions(id).queryKey, [], 0);
  queryClient.setQueryData(getProjectNotesQueryOptions(id).queryKey, []);
  queryClient.setQueryData(projectTasksQueryKey(id), []);
  queryClient.setQueryData(getProjectSessionsQueryOptions(id).queryKey, []);
  queryClient.setQueryData<GlossaryData>(glossaryQueryKey(id), BLANK_GLOSSARY);
  for (const queryKey of blankDraftListKeys(id)) void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' });
}

/** Черновик не создался: убрать засеянное seedBlankDraft (сущность убирает вызывающий вместе со списком черновиков). */
export function removeBlankDraftLists(queryClient: QueryClient, id: string): void {
  for (const queryKey of blankDraftListKeys(id)) queryClient.removeQueries({ queryKey, exact: true });
}

/** Убирает id из списка черновиков (публикация, удаление, discard). Список не загружен или id нет — ничего не трогает. */
export function removeDraftId(queryClient: QueryClient, id: string): void {
  // Зовётся на каждое событие обычного проекта: без записи в кэш, если id в списке нет
  const prev = queryClient.getQueryData<string[]>(projectDraftsQueryKey());
  if (!prev?.includes(id)) return;
  queryClient.setQueryData<string[]>(projectDraftsQueryKey(), (current) => current?.filter((x) => x !== id));
}

/** Черновик появился или изменился в другой вкладке автора: id в начало списка, если его там нет. Список не загружен — не трогает. */
export function addDraftId(queryClient: QueryClient, id: string): void {
  const prev = queryClient.getQueryData<string[]>(projectDraftsQueryKey());
  if (!prev || prev.includes(id)) return;
  queryClient.setQueryData<string[]>(projectDraftsQueryKey(), [id, ...prev]);
}

/** Возвращает id на прежнее место после неудачной публикации или удаления, не затирая изменения, пришедшие за время запроса. */
export function restoreDraftId(queryClient: QueryClient, id: string, index: number): void {
  queryClient.setQueryData<string[]>(projectDraftsQueryKey(), (prev) => {
    if (!prev || prev.includes(id)) return prev;
    const next = [...prev];
    next.splice(Math.min(index, next.length), 0, id);
    return next;
  });
}

/** Загрузки файлов в пути по проектам: от выбора файла до ответа сервера, включая подсчёт листов до отправки. */
const uploadsInFlight = createInFlightTracker();

/**
 * Отмечает загрузку файла проекта на время промиса. Сервер видит файл, только когда загрузка дошла, — до этого
 * черновик для него пуст, и уход из черновика удалил бы его вместе с файлом в пути.
 */
export function trackProjectUpload<T>(projectId: string, upload: Promise<T>): Promise<T> {
  return uploadsInFlight.track(projectId, upload);
}

export function isProjectUploadInFlight(projectId: string): boolean {
  return uploadsInFlight.has(projectId);
}

/** Удаления пустых черновиков в пути: черновик уже убран с экрана, ответ сервера ещё не пришёл. */
const discardsInFlight = createInFlightTracker();

/**
 * Сколько помнить удалённый здесь черновик: эхо его создания (сервер шлёт его до ответа на POST, а discard ждёт ответа)
 * может прийти и после ответа на discard — с запасом на медленный вебсокет.
 */
const DISCARDED_TOMBSTONE_MS = 30_000;
const discardedAt = new Map<string, number>();

/**
 * Отмечает удаление черновика на время промиса (вместе с возвратом при отказе). Пока оно идёт, события realtime
 * и перезапрос списка его не возвращают: исход решает ответ сервера.
 */
export function trackDraftDiscard<T>(projectId: string, discard: Promise<T>): Promise<T> {
  return discardsInFlight.track(projectId, discard);
}

/** Сервер черновик удалил (или его и так нет): запоздавшие события о нём ещё DISCARDED_TOMBSTONE_MS не возвращают его. */
export function markDraftDiscarded(projectId: string): void {
  const now = Date.now();
  for (const [id, at] of discardedAt) if (now - at >= DISCARDED_TOMBSTONE_MS) discardedAt.delete(id);
  discardedAt.set(projectId, now);
}

/** Черновик удаляется здесь или недавно удалён: события и снимки списка о нём устарели. */
export function isDraftDiscarded(projectId: string): boolean {
  if (discardsInFlight.has(projectId)) return true;
  const at = discardedAt.get(projectId);
  return at !== undefined && Date.now() - at < DISCARDED_TOMBSTONE_MS;
}
